import { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowLeft } from 'lucide-react';
import { MarketingConsentSection } from '@/components/privacy/MarketingConsentSection';
import { PartnerVisibilityStoreCard } from '@/components/privacy/PartnerVisibilityStoreCard';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { isRtlLocale } from '@/config/locales';
import { useApp } from '@/contexts/AppContext';
import { useAuth } from '@/contexts/AuthContext';
import { useMarketingConsent } from '@/hooks/useMarketingConsent';
import { usePartnerVisibilityConsents } from '@/hooks/usePartnerVisibilityConsents';
import { getMarketingConsentStrings } from '@/i18n/marketingConsent';
import {
  getPartnerVisibilityStrings,
} from '@/i18n/partnerVisibility';
import type { PartnerVisibilityStatus } from '@/lib/api/types';
import { navigationEvents } from '@/lib/navigation-events';

interface PartnerVisibilitySettingsPageProps {
  onBack: () => void;
}

function PrivacySettingsHeader({
  isRtl,
  onBack,
  back,
  navigationTitle,
}: {
  isRtl: boolean;
  onBack: () => void;
  back: string;
  navigationTitle: string;
}) {
  return (
    <header className="border-b border-border bg-background">
      <div className="mx-auto flex max-w-2xl items-center gap-3 px-4 py-4">
        <button
          type="button"
          onClick={onBack}
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-border bg-background text-foreground"
          aria-label={back}
        >
          <ArrowLeft
            className={`h-5 w-5 ${isRtl ? 'rotate-180' : ''}`}
            aria-hidden="true"
          />
        </button>
        <span className="text-lg font-semibold">{navigationTitle}</span>
      </div>
    </header>
  );
}

interface Attempt {
  status: PartnerVisibilityStatus;
}

interface PageStoreState {
  scopeKey: string | null;
  attempts: ReadonlyMap<number, Attempt>;
  successByStoreId: ReadonlyMap<number, PartnerVisibilityStatus>;
}

const emptyPageStoreState = (scopeKey: string | null): PageStoreState => ({
  scopeKey,
  attempts: new Map(),
  successByStoreId: new Map(),
});

const scopedKeyFor = (
  enabled: boolean,
  customerKey: number | string | null
): string | null =>
  enabled && customerKey !== null
    ? `${typeof customerKey}:${String(customerKey)}`
    : null;

export function PartnerVisibilitySettingsPage({
  onBack,
}: PartnerVisibilitySettingsPageProps) {
  const { locale } = useApp();
  const {
    isCheckingSession,
    isLoggedIn,
    user,
    customerId,
  } = useAuth();
  const strings = getPartnerVisibilityStrings(locale);
  const marketingStrings = getMarketingConsentStrings(locale);
  const customerKey = user?.id ?? customerId;
  const enabled =
    !isCheckingSession && isLoggedIn && customerKey !== null;
  const scopeKey = scopedKeyFor(enabled, customerKey);
  const consentState = usePartnerVisibilityConsents({
    enabled,
    customerKey,
  });
  const marketingState = useMarketingConsent({
    enabled,
    customerKey,
  });
  const [pageStoreState, setPageStoreState] = useState<PageStoreState>(() =>
    emptyPageStoreState(scopeKey)
  );

  const visiblePageStoreState = useMemo(
    () =>
      pageStoreState.scopeKey === scopeKey
        ? pageStoreState
        : emptyPageStoreState(scopeKey),
    [pageStoreState, scopeKey]
  );

  useEffect(() => {
    setPageStoreState(emptyPageStoreState(scopeKey));
  }, [scopeKey]);

  useEffect(() => {
    if (
      scopeKey === null ||
      pageStoreState.scopeKey !== scopeKey ||
      pageStoreState.attempts.size === 0
    ) {
      return;
    }

    let changed = false;
    const nextAttempts = new Map(pageStoreState.attempts);
    const nextSuccess = new Map(pageStoreState.successByStoreId);

    for (const [storeId, attempt] of pageStoreState.attempts) {
      if (
        consentState.pendingStoreIds.has(storeId) ||
        consentState.mutationErrorByStoreId.has(storeId)
      ) {
        continue;
      }

      const store = consentState.stores.find(
        (candidate) => candidate.store_id === storeId
      );
      if (store?.effective_status !== attempt.status) {
        continue;
      }

      nextAttempts.delete(storeId);
      nextSuccess.set(storeId, attempt.status);
      changed = true;
    }

    if (changed) {
      setPageStoreState({
        scopeKey,
        attempts: nextAttempts,
        successByStoreId: nextSuccess,
      });
    }
  }, [
    consentState.mutationErrorByStoreId,
    consentState.pendingStoreIds,
    consentState.stores,
    pageStoreState,
    scopeKey,
  ]);

  const updateStore = useCallback(
    (storeId: number, status: PartnerVisibilityStatus) => {
      if (scopeKey === null) {
        return;
      }

      setPageStoreState((previous) => {
        const scoped =
          previous.scopeKey === scopeKey
            ? previous
            : emptyPageStoreState(scopeKey);
        const attempts = new Map(scoped.attempts);
        const successByStoreId = new Map(scoped.successByStoreId);
        attempts.set(storeId, { status });
        successByStoreId.delete(storeId);

        return {
          scopeKey,
          attempts,
          successByStoreId,
        };
      });
      void consentState.update(storeId, status);
    },
    [consentState, scopeKey]
  );

  const direction = isRtlLocale(locale) ? 'rtl' : 'ltr';
  const pageHeader = (
    <PrivacySettingsHeader
      isRtl={direction === 'rtl'}
      onBack={onBack}
      back={strings.back}
      navigationTitle={marketingStrings.navigationTitle}
    />
  );

  if (isCheckingSession) {
    return (
      <main
        className="min-h-screen bg-muted text-foreground"
        data-state="session-loading"
        dir={direction}
      >
        {pageHeader}
        <div
          className="mx-auto max-w-2xl space-y-4 px-4 py-8"
          aria-live="polite"
          role="status"
        >
          <p>{strings.loading}</p>
          <Skeleton aria-hidden="true" className="h-24 w-full" />
          <Skeleton aria-hidden="true" className="h-24 w-full" />
        </div>
      </main>
    );
  }

  if (!isLoggedIn) {
    return (
      <main
        className="min-h-screen bg-muted text-foreground"
        data-state="login-required"
        dir={direction}
      >
        {pageHeader}
        <div className="flex min-h-[calc(100vh-4.5rem)] items-center justify-center px-4 py-8">
          <section className="w-full max-w-md rounded-lg border bg-background p-6 text-center">
            <h1 className="text-xl font-semibold">
              {marketingStrings.pageHeading}
            </h1>
            <p className="mt-3 text-sm text-muted-foreground">
              {strings.loginRequired}
            </p>
            <Button
              className="mt-5"
              type="button"
              onClick={navigationEvents.openLoginPage}
            >
              {strings.loginCta}
            </Button>
          </section>
        </div>
      </main>
    );
  }

  return (
    <main
      className="min-h-screen bg-muted text-foreground"
      data-state="privacy-settings"
      dir={direction}
    >
      {pageHeader}
      <div className="mx-auto max-w-2xl px-4 py-8">
        <header>
          <h1 className="text-2xl font-semibold">
            {marketingStrings.pageHeading}
          </h1>
        </header>

        <MarketingConsentSection
          key={scopeKey ?? 'anonymous'}
          strings={marketingStrings}
          notice={marketingState.notice}
          consent={marketingState.consent}
          isLoading={marketingState.isInitialLoading}
          loadError={marketingState.initialError !== null}
          isUpdating={marketingState.isUpdating}
          mutationError={marketingState.mutationError !== null}
          isNoticeStale={marketingState.isNoticeStale}
          lastSuccess={marketingState.lastSuccess}
          onRetryLoad={marketingState.retry}
          onUpdate={(status) => {
            void marketingState.update(status);
          }}
        />

        <section
          aria-labelledby="store-privacy-settings-title"
          className="mt-10"
          role="region"
        >
          <h2
            id="store-privacy-settings-title"
            className="text-lg font-semibold"
          >
            {marketingStrings.storeSectionTitle}
          </h2>
          <p className="mt-2 text-sm text-muted-foreground">
            {strings.fulfilledStoresOnlyExplanation}
          </p>

          {consentState.isInitialLoading ? (
            <div
              className="mt-4 space-y-4"
              aria-live="polite"
              role="status"
            >
              <p>{strings.loading}</p>
              <Skeleton aria-hidden="true" className="h-32 w-full" />
              <Skeleton aria-hidden="true" className="h-32 w-full" />
            </div>
          ) : consentState.initialError ? (
            <div className="mt-4 rounded-lg border bg-background p-6 text-center">
              <p className="text-sm text-destructive" role="alert">
                {strings.loadError}
              </p>
              <Button
                className="mt-4"
                type="button"
                variant="outline"
                onClick={consentState.retry}
              >
                {strings.retry}
              </Button>
            </div>
          ) : (
            <>
              {consentState.notice ? (
                <div className="mt-4 rounded-lg border bg-background p-4">
                <a
                  className="font-medium text-primary underline underline-offset-4"
                  href={consentState.notice.url}
                >
                  {strings.privacyNoticeLink}
                </a>
                <p className="mt-2 text-sm text-muted-foreground">
                  {strings.privacyNoticeVersionLabel}:{' '}
                  <bdi dir="ltr">{consentState.notice.version}</bdi>
                </p>
                </div>
              ) : null}

              {consentState.stores.length === 0 ? (
                <p className="mt-6 rounded-lg border bg-background p-6 text-center text-sm text-muted-foreground">
                  {strings.emptyFulfilledStores}
                </p>
              ) : (
                <div className="mt-6 space-y-4">
                  {consentState.stores.map((store) => {
                  const attemptedStatus =
                    visiblePageStoreState.attempts.get(store.store_id)?.status;
                  const successfulStatus =
                    visiblePageStoreState.successByStoreId.get(store.store_id);
                  const mutationMessage =
                    consentState.mutationErrorByStoreId.get(store.store_id);
                  const mutationError =
                    attemptedStatus && mutationMessage
                      ? {
                          message: strings.mutationError,
                          status: attemptedStatus,
                        }
                      : null;

                  return (
                    <PartnerVisibilityStoreCard
                      key={store.store_id}
                      store={store}
                      strings={strings}
                      pending={consentState.pendingStoreIds.has(store.store_id)}
                      error={mutationError}
                      status={
                        successfulStatus === 'granted'
                          ? strings.grantSuccess
                          : successfulStatus === 'withdrawn'
                            ? strings.withdrawSuccess
                            : null
                      }
                      onGrant={(storeId) => updateStore(storeId, 'granted')}
                      onWithdraw={(storeId) =>
                        updateStore(storeId, 'withdrawn')
                      }
                      onRetry={updateStore}
                    />
                  );
                  })}
                </div>
              )}

              {consentState.loadMoreError ? (
                <p className="mt-4 text-center text-sm text-destructive" role="alert">
                  {strings.paginationError}
                </p>
              ) : null}

              {consentState.nextCursor !== null ? (
                <div className="mt-6 text-center">
                  <Button
                    disabled={consentState.isLoadingMore}
                    type="button"
                    variant="outline"
                    onClick={() => {
                      void consentState.loadMore();
                    }}
                  >
                    {consentState.isLoadingMore
                      ? strings.loadingMore
                      : strings.loadMore}
                  </Button>
                </div>
              ) : null}
            </>
          )}
        </section>
      </div>
    </main>
  );
}
